import cn from 'classnames';

interface DataGridPaginationProps {
  enablePagination: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  table: any;
  pagination: { pageIndex: number; pageSize: number };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setPagination: (updater: any) => void;
  totalItems?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
  manualPagination: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchParams: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setSearchParams: (params: any) => void;
  pageSizeOptions: number[];
  isPaginating: boolean;
  canPreviousPage: boolean;
  canNextPage: boolean;
  currentPage: number;
  totalPages: number;
  getPageNumbers: () => (number | 'gap')[];
  themeText: string;
  themeLayer: string;
  themeBorder: string;
  footerBg: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getColour: (element: any) => string;
  getBrandTint: (opacity: number) => string;
  t: (key: string) => string;
}

export function DataGridPagination({
  enablePagination,
  table,
  pagination,
  setPagination,
  totalItems,
  data,
  manualPagination,
  searchParams,
  setSearchParams,
  pageSizeOptions,
  isPaginating,
  canPreviousPage,
  canNextPage,
  currentPage,
  totalPages,
  getPageNumbers,
  themeText,
  themeLayer,
  themeBorder,
  footerBg,
  getColour,
  getBrandTint,
  t
}: DataGridPaginationProps) {
  if (!enablePagination) return null;

  return (
    <div
      className="flex flex-row items-center justify-between gap-2 px-3 rounded-b-2xl text-[11px]"
      style={{
        color: themeText,
        background: footerBg,
        borderTop: `1px solid ${themeBorder}`,
        height: '44px'
      }}
    >
      {/* Items count */}
      <div className="flex items-center gap-1.5 sm:gap-2">
        <span className="whitespace-nowrap opacity-70">
          {totalItems ?? data.length} {t('items') || 'items'}
        </span>
        {isPaginating && (
          <span
            className="inline-block h-2.5 w-2.5 animate-spin rounded-full border border-t-transparent shrink-0"
            style={{
              borderColor: `${getColour('inactiveText')} transparent transparent transparent`
            }}
          />
        )}
        <select
          value={pagination.pageSize}
          aria-label={t('rows-per-page') || 'Rows per page'}
          onChange={e => {
            const size = Number(e.target.value);
            table.setPageSize(size);
            if (manualPagination) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              setPagination((p: any) => ({
                ...p,
                pageSize: size,
                pageIndex: 0
              }));
              setSearchParams({
                offset: '0',
                limit: String(size),
                q: searchParams?.q ?? ''
              });
            }
          }}
          className="h-7 px-2 border rounded-2xl focus:outline-none focus:ring-1"
          style={{
            borderColor: themeBorder,
            backgroundColor: themeLayer,
            color: themeText
          }}
        >
          {pageSizeOptions.map(size => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>

      {/* Pager */}
      <div className="flex items-center gap-1 sm:gap-2">
        <button
          onClick={() =>
            !isPaginating && canPreviousPage && table.previousPage()
          }
          disabled={!canPreviousPage || isPaginating}
          className={cn(
            'inline-flex items-center gap-1 px-2 py-1 rounded-full font-medium transition-colors',
            !canPreviousPage || isPaginating
              ? 'opacity-40 cursor-not-allowed'
              : 'hover:bg-black/5 dark:hover:bg-white/8 cursor-pointer'
          )}
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 16 16"
          >
            <path
              d="M2.75 8H13.25M2.75 8L5.25 5.5M2.75 8L5.25 10.5"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>Previous</span>
        </button>

        <div
          className="flex sm:hidden items-center px-2 py-0.5 rounded-full"
          style={{
            backgroundColor: getBrandTint(0.1),
            color: getColour('activeText')
          }}
        >
          <span className="font-medium text-[11px]">
            {currentPage} / {totalPages}
          </span>
        </div>

        <div className="hidden sm:flex items-center gap-1">
          {getPageNumbers().map((page, idx) =>
            page === 'gap' ? (
              <span key={`gap-${idx}`} className="w-8 text-center select-none">
                &hellip;
              </span>
            ) : (
              <button
                key={page}
                onClick={() => !isPaginating && table.setPageIndex(page - 1)}
                disabled={isPaginating}
                className={cn(
                  'min-w-8 h-7 px-2 rounded-full font-medium transition-colors',
                  page !== currentPage &&
                    'hover:bg-black/5 dark:hover:bg-white/8',
                  isPaginating && 'pointer-events-none opacity-60'
                )}
                style={
                  page === currentPage
                    ? {
                        backgroundColor: getBrandTint(0.12),
                        color: getColour('activeText')
                      }
                    : undefined
                }
              >
                {page}
              </button>
            )
          )}
        </div>

        <button
          onClick={() => !isPaginating && canNextPage && table.nextPage()}
          disabled={!canNextPage || isPaginating}
          className={cn(
            'inline-flex items-center gap-1 px-2 py-1 rounded-full font-medium transition-colors',
            !canNextPage || isPaginating
              ? 'opacity-40 cursor-not-allowed'
              : 'hover:bg-black/5 dark:hover:bg-white/8 cursor-pointer'
          )}
        >
          <span>Next</span>
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 16 16"
          >
            <path
              d="M13.25 8L2.75 8M13.25 8L10.75 10.5M13.25 8L10.75 5.5"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
